import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Upload, X, Image, Video, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use raw backend URL for file serving (URLs already include /api)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FileUpload = ({
  entityType = 'booth',  // 'booth' or 'product'
  entityId,
  mediaType = 'image',   // 'logo', 'banner', 'image', 'video'
  currentUrl,
  onUploadSuccess,
  onRemove,
  accept = 'image/*',
  maxSize = 5,           // MB
  label = 'Upload',
  previewClassName = 'w-full h-32',
  showPreview = true,
  multiple = false,      // Allow multiple file uploads
  fillWidth = false,     // For banners - fill width completely
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Sync preview with currentUrl when it changes (e.g., opening edit modal with different item)
  React.useEffect(() => {
    setPreview(currentUrl);
  }, [currentUrl]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // For single file mode, take just the first file
    const filesToUpload = multiple ? files : [files[0]];

    // Validate all file sizes
    for (const file of filesToUpload) {
      if (file.size > maxSize * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is ${maxSize}MB`);
        return;
      }
    }

    // Create preview for single image (not in multiple mode)
    if (!multiple && filesToUpload[0].type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(filesToUpload[0]);
    } else if (!multiple && filesToUpload[0].type.startsWith('video/')) {
      setPreview('video');
    }

    // Upload files
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const totalFiles = filesToUpload.length;
      let uploadedCount = 0;
      let lastUrl = null;
      
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);

        let endpoint;
        if (entityType === 'booth') {
          endpoint = `${BACKEND_URL}/api/upload/booth/${entityId}?media_type=${mediaType}`;
        } else if (entityType === 'event') {
          endpoint = `${BACKEND_URL}/api/upload/event/${entityId}`;
        } else {
          endpoint = `${BACKEND_URL}/api/upload/product/${entityId}`;
        }

        const response = await axios.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        lastUrl = response.data.url;
        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      }

      if (!multiple) {
        setPreview(lastUrl);
      }
      
      if (onUploadSuccess) {
        onUploadSuccess(lastUrl, { count: uploadedCount });
      }
      
      toast.success(multiple && totalFiles > 1 
        ? `${uploadedCount} files uploaded successfully` 
        : 'File uploaded successfully'
      );
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      if (!multiple) {
        setPreview(currentUrl);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (onRemove) {
      onRemove();
    }
  };

  const isVideo = preview === 'video' || (preview && (preview.includes('.mp4') || preview.includes('.webm') || preview.includes('.mov')));

  return (
    <div className="space-y-2">
      {/* Preview Area */}
      {showPreview && preview && (
        <div className={`relative ${previewClassName} rounded-lg overflow-hidden bg-gradient-to-b from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center`}>
          {isVideo ? (
            <div className="w-full h-full flex items-center justify-center bg-slate-900">
              {preview !== 'video' ? (
                <video 
                  src={preview.startsWith('/api') ? `${BACKEND_URL}${preview}` : preview}
                  className="max-w-full max-h-full object-contain"
                  controls
                />
              ) : (
                <Video className="w-12 h-12 text-slate-400" />
              )}
            </div>
          ) : (
            <img
              src={preview.startsWith('/api') ? `${BACKEND_URL}${preview}` : preview}
              alt="Preview"
              className={fillWidth ? "w-full h-full object-cover" : "max-w-full max-h-full object-contain"}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          {!uploading && (
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
        </div>
      )}

      {/* Upload Button */}
      {!preview && (
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`${previewClassName} border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
              {uploadProgress > 0 && (
                <span className="text-xs text-slate-500 mt-1">{uploadProgress}%</span>
              )}
            </div>
          ) : (
            <>
              {mediaType === 'video' ? (
                <Video className="w-8 h-8 text-slate-400 mb-2" />
              ) : (
                <Image className="w-8 h-8 text-slate-400 mb-2" />
              )}
              <span className="text-sm text-slate-500">{label}</span>
              <span className="text-xs text-slate-400 mt-1">Max {maxSize}MB{multiple ? ' each' : ''}</span>
            </>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        multiple={multiple}
      />

      {/* Change Button (when preview exists) */}
      {preview && !uploading && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
        >
          <Upload className="w-4 h-4 mr-2" />
          Change {mediaType === 'video' ? 'Video' : 'Image'}
        </Button>
      )}
    </div>
  );
};

export default FileUpload;
